import classNames from 'classnames/bind';
import styles from './StudentActivityInfo.module.css';
import { useLocation } from 'react-router';
import AliasHeading from '../AliasHeading';
import {
  COLLECTED_INFO_MESSAGE,
  COLLECTING_INFORMATION,
  DAILY_STUDY_COUNT,
  MAX_CONCENTRATION_TIME,
  NOTE_USAGE_TIME,
  PRIMARY_STUDY_DAYS,
  PRIMARY_STUDY_HOURS,
  STUDENT_ACTIVITY_SUFFIX,
  TOTAL_STUDY_TIME,
} from '../../../constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { analysisApiSlice } from '../../../services/analysis';
import { useEffect, useState } from 'react';
import ClassStudentInfo from '../ClassStudentInfo';
import {
  selectStudentProjectId,
  selectStudentSummary,
  studentAnalysisChanged,
  studentProjectIdChanged,
  studentProjectListChanged,
  studentUserIdChanged,
} from '../../../features/analysisSlice';
import { AnalyticsProps, SummaryDuration } from '../../../types/analysis';
import DateRangePicker from '../../../components/DataRangePicker';
import { getDiff } from '../../../utils/date';
import Library from '../Library';

export default function StudentActivityInfo() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const {
    state: { userId },
  } = useLocation();

  const [getClassStudent] = analysisApiSlice.useLazyGetClassStudentQuery();
  const [getClassProjects, { isSuccess }] =
    analysisApiSlice.useLazyGetClassProjectListQuery();

  const [studentData, setStudentData] = useState();
  const [duration, setDuration] = useState<SummaryDuration>();
  const [alias, setAlias] = useState('');
  const [name, setName] = useState('');

  const summary = useAppSelector(selectStudentSummary);
  const projectId = useAppSelector(selectStudentProjectId);

  const getStudentData = async () => {
    try {
      const {
        data: { alias, nm, studentMgmt, studentAnalysis },
      } = await getClassStudent({ userId, duration });
      setAlias(alias);
      setName(nm);
      setStudentData(studentMgmt);
      dispatch(studentAnalysisChanged(studentAnalysis));
    } catch (error) {
      console.error('Failed to get student data', error);
    }
  };

  useEffect(() => {
    dispatch(studentUserIdChanged(userId));
  }, []);

  useEffect(() => {
    getStudentData();
  }, [userId, duration]);

  if (!studentData) return null;

  const { regDate, today, total, timeZone, weekZone, daily, attention, note } =
    summary;
  const { startDate = '', endDate = '', totalTime } = total;

  const analytics: AnalyticsProps[] = [
    {
      title: TOTAL_STUDY_TIME,
      key: 'total',
      value: totalTime,
    },
    {
      title: PRIMARY_STUDY_HOURS,
      key: 'timeZone',
      value: timeZone.best,
    },
    {
      title: PRIMARY_STUDY_DAYS,
      key: 'weekZone',
      value: weekZone.best,
    },
    { title: DAILY_STUDY_COUNT, key: 'daily', value: daily.avg },
    {
      title: MAX_CONCENTRATION_TIME,
      key: 'attention',
      value: attention.time,
    },
    { title: NOTE_USAGE_TIME, key: 'note', value: note.time },
  ];

  return (
    <>
      <AliasHeading
        alias={alias}
        name={name}
        suffix={STUDENT_ACTIVITY_SUFFIX}
        hasBackButton={true}
      />

      <div className={cx('dash_section')}>
        <ClassStudentInfo data={studentData} />
      </div>

      <div className={cx('dash_date')}>
        <div className={cx('date')}>
          ※
          {getDiff(today, regDate) ? (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              startPicker={{ min: regDate, max: endDate }}
              endPicker={{ min: startDate, max: today }}
              onChange={(startDate: string, endDate: string) =>
                setDuration({ startDate, endDate })
              }
              descSuffix={COLLECTED_INFO_MESSAGE}
            />
          ) : (
            <span>{COLLECTING_INFORMATION}</span>
          )}
        </div>
      </div>

      <Library
        type="sharedByMe"
        projects={{
          queryTrigger: getClassProjects,
          isSuccess,
          projectListChanged: studentProjectListChanged,
          projectIdChanged: studentProjectIdChanged,
        }}
        projectId={projectId}
        userId={userId}
      />
    </>
  );
}
